import React from 'react';
import { formatTimeMs } from '../game/leaderboard';
import type { GameMode, VersusTeam } from '../game/types';

interface GameBarProps {
  mode?: GameMode;
  onBackMenu: () => void;
  elapsedMs: number;
  blitzTimeLeftMs?: number;
  versusBlueScore?: number;
  versusRedScore?: number;
  versusActiveTeam?: VersusTeam | null;
  versusSelectionTimeLeftMs?: number;
  onlineOpponentSets?: number;
  onlineOpponentCardsLeft?: number;
  onlineOpponentTimeLeftMs?: number;
  cardsCount: number;
  onHint: () => void;
  onHelp: () => void;
  showBonusTime?: boolean;
}

export const GameBar: React.FC<GameBarProps> = ({
  mode = 'classic',
  onBackMenu,
  elapsedMs,
  blitzTimeLeftMs = 0,
  versusBlueScore = 0,
  versusRedScore = 0,
  versusActiveTeam = null,
  versusSelectionTimeLeftMs = 0,
  onlineOpponentSets = 0,
  onlineOpponentCardsLeft = 81,
  onlineOpponentTimeLeftMs = 30000,
  cardsCount,
  onHint,
  onHelp,
  showBonusTime = false,
}) => {
  const isBlitz = mode === 'blitz' || mode === 'online_blitz';
  const isOnlineBlitz = mode === 'online_blitz';
  const isVersus = mode === 'versus' || mode === 'online_clash';
  const isRace = mode === 'online_race';

  return (
    <div className={`game-bar ${isBlitz ? 'game-bar-blitz' : ''} ${isVersus ? 'game-bar-versus' : ''} ${isRace ? 'game-bar-race' : ''} ${isOnlineBlitz ? 'game-bar-online-blitz' : ''}`}>
      <div className="game-bar-left">
        <div className="game-bar-nav-buttons">
          <button className="btn-icon btn-home-icon" id="btn-back-menu" onClick={onBackMenu} title="Main Menu" aria-label="Main Menu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </button>
          <button className="btn-icon btn-help-icon" id="btn-help" onClick={onHelp} title="How to Play" aria-label="How to Play">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </button>
        </div>
        {isVersus && (
          <div className="versus-team-badge badge-blue" id="versus-score-blue" title={mode === 'online_clash' ? 'Your Score (Blue)' : 'Blue Team Score'}>
            <span className="team-dot dot-blue" />
            <span className="team-score">{versusBlueScore}</span>
          </div>
        )}
      </div>

      <div className="game-bar-center">
        <div className="status-pill">
          {isVersus ? (
            <>
              <span
                className={`status-item status-timer ${versusActiveTeam ? `versus-timer-${versusActiveTeam}` : ''}`}
                id="timer-display"
                title={versusActiveTeam ? `${versusActiveTeam.toUpperCase()} Selection Time` : 'Ready to Tap'}
              >
                {versusActiveTeam ? `${Math.ceil(versusSelectionTimeLeftMs / 1000)}s` : '--'}
              </span>
              <span className="status-divider">•</span>
              <span className="status-item status-deck" id="cards-left-display" title="Cards remaining (deck + board)">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="6" y="2" width="14" height="17" />
                  <path d="M2 6v14a2 2 0 0 0 2 2h13" />
                </svg>
                <span>{cardsCount}</span>
              </span>
            </>
          ) : (
            <>
              <div className="timer-container">
                <span
                  className={`status-item status-timer ${isBlitz && blitzTimeLeftMs <= 10000 ? 'timer-warning' : ''}`}
                  id="timer-display"
                  title={isBlitz ? 'Time Remaining' : 'Elapsed Time'}
                >
                  {isBlitz ? formatTimeMs(blitzTimeLeftMs) : formatTimeMs(elapsedMs)}
                </span>
                {showBonusTime && <span className="bonus-time-pop">+10s</span>}
              </div>
              <span className="status-divider">•</span>
              <span className="status-item status-deck" id="cards-left-display" title="Cards remaining (deck + board)">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="6" y="2" width="14" height="17" />
                  <path d="M2 6v14a2 2 0 0 0 2 2h13" />
                </svg>
                <span>{cardsCount}</span>
              </span>
            </>
          )}
        </div>
      </div>

      {isVersus ? (
        <div className="game-bar-right-versus">
          <div className="versus-team-badge badge-red" id="versus-score-red" title={mode === 'online_clash' ? 'Opponent Score (Red)' : 'Red Team Score'}>
            <span className="team-score">{versusRedScore}</span>
            <span className="team-dot dot-red" />
          </div>
        </div>
      ) : isRace ? (
        <div className="race-opponent-badge" title="Opponent Progress">
          <span className="opponent-icon">🏎️</span>
          <span className="opponent-score">{onlineOpponentSets}</span>
          <span className="opponent-unit">sets</span>
        </div>
      ) : isOnlineBlitz ? (
        <div className="blitz-opponent-badge" title="Opponent Status">
          <span className="opponent-sets-wrap">
            <span className="opponent-icon">⚡</span>
            <span className="opponent-score">{onlineOpponentSets}</span>
            <span className="opponent-unit">sets</span>
          </span>
          <span className="opponent-divider">•</span>
          <span className={`opponent-time ${onlineOpponentTimeLeftMs <= 10000 ? 'opponent-time-low' : ''}`}>
            {Math.ceil(onlineOpponentTimeLeftMs / 1000)}s
          </span>
        </div>
      ) : isBlitz ? (
        <div className="game-bar-right-spacer" aria-hidden="true" />
      ) : (
        <button className="btn-hint" id="btn-hint" onClick={onHint} title="Hint (+15s penalty)" aria-label="Hint">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18h6" />
            <path d="M10 22h4" />
            <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5.76.76 1.23 1.52 1.41 2.5" />
          </svg>
          <span className="hint-badge">+15s</span>
        </button>
      )}
    </div>
  );
};
